import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Check,
  ChevronDown,
  Code2,
  Lock,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  UploadCloud,
  Wand2,
} from 'lucide-react';
import kgDatasetService from '../../../services/kgDatasetService';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import KgDryRunReport from '../KgDryRunReport';
import KgInspectPanel from '../KgInspectPanel';
import PathField from '../PathField';
import PreprocessingSection from '../PreprocessingSection';
import styles from './MappingTab.module.scss';
import {
  DATE_KIND_OPTIONS,
  DATE_UNIT_OPTIONS,
  DRY_RUN_DEBOUNCE_MS,
  DRY_RUN_SAMPLE_SIZE,
  FACET_NAME_RE,
  RECORD_KEY_LOCK_REASON,
  TEXT_LOCK_REASON,
  dateKindUsesUnit,
  describeError,
  fieldsFromMappingConfig,
  formatNumber,
  humanizeLabel,
  isPreprocessingLocked,
  isRecordKeyLocked,
  isTextLocked,
  normalizeMappingPaths,
  parseRecordKey,
  preprocessingNamespace,
  serializePreprocessing,
  readRecordsFromFiles,
  serializeRecordKey,
  slugifyFacetName,
  textConfigChanged,
  validateMappingConfig,
  validateRecordKey,
} from '../kgUtils';

const clone = (v) => JSON.parse(JSON.stringify(v));

const emptyFacet = (index) => ({
  name: '',
  label: '',
  path: '',
  kind: 'scalar',
  cardinality: 'one',
  order_index: index + 1,
  enabled: true,
  note: '',
  normalize: { trim: true, collapse_ws: true, case: null, max_len: 500, aliases: {} },
});

/**
 * Mapping editor (screen 3) — the entire configuration surface.
 *
 * Nothing about a customer's schema is hardcoded anywhere in the platform, so
 * this form IS the product for anyone setting a dataset up. Two things it is
 * built around:
 *
 *   • Dry-run runs on every edit and its report is shown in full, never as a
 *     pass/fail. A facet at 0% coverage is the most useful signal in the whole
 *     feature and it only ever appears here.
 *   • The text section locks once records exist. Text bodies are not retained —
 *     they are already chunked into the vector store — so the API rejects a
 *     change with a 400. Locking is kinder than letting someone redo the work
 *     and then find out.
 */
const MappingTab = ({
  dataset,
  samples,
  sampleFields,
  inspection,
  inspecting,
  onSamples,
  onMutated,
  notify,
}) => {
  const [config, setConfig] = useState(() => clone(dataset.mapping_config || {}));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunError, setDryRunError] = useState(null);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [quoteDiff, setQuoteDiff] = useState(null);
  const [openSections, setOpenSections] = useState({
    relations: false,
    advanced: false,
    columns: false,
  });
  const [aliasDrafts, setAliasDrafts] = useState({});
  const [expandedFacets, setExpandedFacets] = useState({});
  // An "and…" row opened but not filled. Kept out of the config: a "" in
  // record_key is a 400, and serializeRecordKey drops it anyway — which is why
  // pushing "" into the config made the Add button silently do nothing.
  const [pendingKeyColumn, setPendingKeyColumn] = useState(false);

  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);

  const textLocked = isTextLocked(dataset);
  // Preprocessing decides record identity and searchable text, so it freezes on
  // the same event the text section does.
  const preprocessingLocked = isPreprocessingLocked(dataset);
  // record_key normally stays editable — it only changes what the NEXT push
  // updates. Once it is DERIVED, editing it means editing a frozen derivation.
  const recordKeyLocked = isRecordKeyLocked(dataset, config);
  const recordKeyPaths = parseRecordKey(config.record_key);
  const recordKeyError = validateRecordKey(recordKeyPaths);
  const derivedFieldNames = useMemo(
    () =>
      (config.preprocessing?.fields || [])
        .map((f) => String(f?.name || '').trim())
        .filter(Boolean),
    [config.preprocessing]
  );
  const recordKeyLabel = recordKeyPaths.join(' + ');

  /**
   * What the column pickers offer.
   *
   * `sampleFields` is empty until a sample file is loaded in this session — and
   * it is session state, so reopening a fully configured dataset emptied every
   * picker, which reads as "this dataset has no columns". The paths the saved
   * config already uses are the honest fallback: enough to edit an existing
   * mapping, and PathField says the list is partial so nobody mistakes it for
   * the real column list.
   */
  const hasSample = sampleFields.length > 0;
  const pickerFields = useMemo(
    () => (hasSample ? sampleFields : fieldsFromMappingConfig(config)),
    [hasSample, sampleFields, config]
  );
  const validationErrors = useMemo(() => {
    const errors = validateMappingConfig(config, { datasetKey: dataset.key });
    // The form disables the text inputs once records exist, but the JSON
    // escape hatch goes straight past that — and a changed (path, embed) pair on
    // a populated dataset is a guaranteed 400. Catch it here so the lock actually
    // holds on every route into the config, not just the one with the switches.
    if (textLocked && textConfigChanged(config, dataset.mapping_config)) {
      errors.push(
        'Text fields cannot change once records are loaded. Revert the text section, or create a new dataset — the original text is not kept, so re-mapping it is only possible by re-pushing.'
      );
    }
    // Same reasoning for the two blocks that were frozen alongside it. The JSON
    // editor goes straight past the disabled inputs, so the lock has to hold
    // here as well or it only holds on one route into the config.
    if (
      preprocessingLocked &&
      JSON.stringify(config.preprocessing || null) !==
        JSON.stringify(dataset.mapping_config?.preprocessing || null)
    ) {
      errors.push(
        'Input normalization cannot change once records are loaded — it decides how each record is identified and what text is searchable. Revert it, or start a new dataset.'
      );
    }
    if (
      recordKeyLocked &&
      JSON.stringify(config.record_key) !== JSON.stringify(dataset.mapping_config?.record_key)
    ) {
      errors.push(RECORD_KEY_LOCK_REASON);
    }
    return errors;
  }, [config, dataset.key, dataset.mapping_config, textLocked, preprocessingLocked, recordKeyLocked]);
  const dirty = useMemo(
    () => JSON.stringify(config) !== JSON.stringify(dataset.mapping_config || {}),
    [config, dataset.mapping_config]
  );

  // ── Dry-run: debounced, free, writes nothing ────────────────────────────

  const runDryRun = useCallback(
    async (cfg) => {
      if (!samples?.length) return;
      setDryRunning(true);
      setDryRunError(null);
      try {
        const response = await kgDatasetService.dryRun(cfg, samples.slice(0, DRY_RUN_SAMPLE_SIZE));
        setDryRun(response);
      } catch (e) {
        setDryRunError(describeError(e, "Couldn't validate this mapping."));
      } finally {
        setDryRunning(false);
      }
    },
    [samples]
  );

  useEffect(() => {
    if (!samples?.length) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runDryRun(config), DRY_RUN_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [config, samples, runDryRun]);

  // ── Config mutation helpers ────────────────────────────────────────────

  const patch = (updater) =>
    setConfig((prev) => {
      const next = clone(prev);
      updater(next);
      return next;
    });

  const patchList = (section, index, key, value) =>
    patch((next) => {
      if (!Array.isArray(next[section])) next[section] = [];
      next[section][index] = { ...next[section][index], [key]: value };
    });

  const removeFrom = (section, index) =>
    patch((next) => {
      next[section].splice(index, 1);
    });

  // ── record_key (may be composite) ──────────────────────────────────────

  const setRecordKeyAt = (index, value) =>
    patch((next) => {
      const list = parseRecordKey(next.record_key);
      list[index] = value;
      next.record_key = serializeRecordKey(list.filter(Boolean));
    });

  const removeRecordKeyColumn = (index) =>
    patch((next) => {
      const list = parseRecordKey(next.record_key);
      list.splice(index, 1);
      next.record_key = serializeRecordKey(list);
    });

  const moveRecordKeyColumn = (index, delta) =>
    patch((next) => {
      const list = parseRecordKey(next.record_key);
      const target = index + delta;
      if (target < 0 || target >= list.length) return;
      [list[index], list[target]] = [list[target], list[index]];
      next.record_key = serializeRecordKey(list);
    });

  // ── Unmapped-field one-click actions ───────────────────────────────────

  const handleAddFacet = (field) => {
    const taken = new Set((config.facets || []).map((f) => f.name));
    let name = slugifyFacetName(field);
    let n = 2;
    while (taken.has(name)) {
      name = `${slugifyFacetName(field)}_${n}`;
      n += 1;
    }
    const known = sampleFields.find((f) => f.depth === 1 && f.segments[0].name === field);
    patch((next) => {
      if (!Array.isArray(next.facets)) next.facets = [];
      next.facets.push({
        ...emptyFacet(next.facets.length),
        name,
        label: humanizeLabel(field),
        path: known ? known.path : field,
        cardinality: known?.multiValued || known?.isList ? 'many' : 'one',
        kind: known?.isList ? 'nested' : 'scalar',
      });
      next.ignore = (next.ignore || []).filter((i) => i !== field);
    });
    notify(`Added "${name}" as a facet.`, 'success');
  };

  const handleAddIgnore = (field) => {
    patch((next) => {
      next.ignore = [...new Set([...(next.ignore || []), field])];
    });
    notify(`"${field}" will no longer be reported as unmapped.`, 'info');
  };

  // ── Aliases ────────────────────────────────────────────────────────────
  // Observed values come from the loaded sample rather than the dry-run report,
  // which only carries coverage counts. Seeing the actual variants beside the
  // editor is what makes collapsing them possible.

  const observedValues = (facetPath) => {
    const field = sampleFields.find((f) => f.path === facetPath);
    return field ? field.values.filter((v) => typeof v === 'string') : [];
  };

  const setAlias = (facetIndex, from, to) =>
    patch((next) => {
      const facet = next.facets[facetIndex];
      if (!facet.normalize) facet.normalize = {};
      const aliases = { ...(facet.normalize.aliases || {}) };
      if (to) aliases[from] = to;
      else delete aliases[from];
      facet.normalize.aliases = aliases;
    });

  // ── JSON escape hatch ──────────────────────────────────────────────────

  const openJson = () => {
    setJsonText(JSON.stringify(config, null, 2));
    setJsonError(null);
    setJsonMode(true);
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      // A pasted config is the one route that bypasses the column pickers, so
      // re-quote every path in it — and show what changed rather than rewriting
      // it silently.
      const { config: normalized, changed } = normalizeMappingPaths(parsed);
      normalized.dataset = { ...(normalized.dataset || {}), key: dataset.key };
      setConfig(normalized);
      setQuoteDiff(changed.length ? changed : null);
      setJsonMode(false);
      setJsonError(null);
    } catch (e) {
      setJsonError(`Not valid JSON: ${e.message}`);
    }
  };

  // ── Sample loading (drives the column pickers) ─────────────────────────

  const readSample = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const { records, errors } = await readRecordsFromFiles(files);
    if (records.length === 0) {
      notify(
        errors[0] || 'No records found in that selection.',
        'error'
      );
      return;
    }
    // Capped deliberately: this only drives column suggestions and the live
    // check, and walking a 100k-record export on every keystroke would stall
    // the editor for no extra signal.
    const sample = records.slice(0, 200);
    onSamples(sample);
    notify(
      `${formatNumber(sample.length)} records loaded — column suggestions and live checking are on.`,
      'success'
    );
  };

  // ── Save ───────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const body = clone(config);
      // The API rejects a mismatch with a 400; force it rather than trusting the form.
      body.dataset = { ...(body.dataset || {}), key: dataset.key };
      // Drop the normalization block's empty scaffolding — a candidate row added
      // and then abandoned shouldn't become part of the stored config.
      const preprocessing = serializePreprocessing(body.preprocessing);
      if (preprocessing) body.preprocessing = preprocessing;
      else delete body.preprocessing;
      await kgDatasetService.putMapping(dataset.id, body);
      notify('Mapping saved. The facet catalog has been refreshed.', 'success');
      await onMutated({ reloadFacets: true });
    } catch (e) {
      // Mapping validation errors come back flat and readable by design.
      setSaveError(describeError(e, "Couldn't save the mapping."));
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Render ─────────────────────────────────────────────────────────────

  const facets = config.facets || [];
  const dates = config.dates || [];
  const text = config.text || [];

  return (
    <div className={styles.layout}>
      {/* ══ Left: the form ══ */}
      <div className={styles.formColumn}>
        <div className={styles.formToolbar}>
          <Button
            size="small"
            variant="text"
            startIcon={<Code2 size={14} />}
            onClick={jsonMode ? () => setJsonMode(false) : openJson}
          >
            {jsonMode ? 'Back to form' : 'Edit as JSON'}
          </Button>
          <div className={styles.toolbarRight}>
            {dirty && (
              <Button
                size="small"
                variant="text"
                startIcon={<RotateCcw size={14} />}
                onClick={() => {
                  setPendingKeyColumn(false);
                  setConfig(clone(dataset.mapping_config || {}));
                }}
              >
                Discard changes
              </Button>
            )}
            {/* Never a disabled primary button here. `.primaryBtn` forces its
                background, so MUI's disabled state barely dims it — it stays
                looking clickable while doing nothing, which reads as broken.
                Say which of the two reasons applies instead. */}
            {!dirty ? (
              <span className={styles.savedTag}>
                <Check size={13} /> All changes saved
              </span>
            ) : (
              <Tooltip
                title={validationErrors.length ? validationErrors.join(' ') : ''}
                placement="top"
              >
                <span>
                  <Button
                    size="small"
                    variant={validationErrors.length ? 'outlined' : 'contained'}
                    className={
                      validationErrors.length ? sharedStyles.outlineBtn : sharedStyles.primaryBtn
                    }
                    startIcon={saving ? <CircularProgress size={13} /> : <Save size={14} />}
                    onClick={handleSave}
                    disabled={saving || validationErrors.length > 0}
                  >
                    {saving
                      ? 'Saving…'
                      : validationErrors.length
                        ? `Fix ${validationErrors.length} problem${validationErrors.length === 1 ? '' : 's'} to save`
                        : 'Save mapping'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {saveError && (
          <Alert severity="error" onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        {quoteDiff && (
          <Alert severity="info" onClose={() => setQuoteDiff(null)}>
            <strong>Quoted {quoteDiff.length} path{quoteDiff.length === 1 ? '' : 's'} for you.</strong>
            <ul className={styles.diffList}>
              {quoteDiff.map((c, i) => (
                <li key={i}>
                  <code>{c.from}</code> → <code>{c.to}</code> <em>({c.where})</em>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {validationErrors.length > 0 && (
          <Alert severity="warning">
            <ul className={styles.errorList}>
              {validationErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </Alert>
        )}

        {jsonMode ? (
          <div className={styles.jsonBlock}>
            <TextField
              multiline
              minRows={24}
              fullWidth
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              error={Boolean(jsonError)}
              helperText={jsonError}
              InputProps={{ className: styles.jsonInput }}
            />
            <Button variant="contained" className={sharedStyles.primaryBtn} onClick={applyJson}>
              Apply
            </Button>
          </div>
        ) : (
          <>
            {/* ── 1. Dataset ── */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Dataset</h3>
              <p className={styles.sectionHelp}>
                What the agent calls these records when it answers — “12{' '}
                {config.dataset?.record_label_plural || 'records'}”, not “12 rows”.
              </p>
              <div className={styles.row2}>
                <TextField
                  size="small"
                  label="One record is a…"
                  value={config.dataset?.record_label_singular || ''}
                  onChange={(e) =>
                    patch((next) => {
                      next.dataset = { ...(next.dataset || {}), record_label_singular: e.target.value };
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Many are…"
                  value={config.dataset?.record_label_plural || ''}
                  onChange={(e) =>
                    patch((next) => {
                      next.dataset = { ...(next.dataset || {}), record_label_plural: e.target.value };
                    })
                  }
                />
              </div>
            </section>

            {/* ── 2. Input normalization ── above identity, because the row
                identity may be one of the fields it derives. ── */}
            <PreprocessingSection
              config={config}
              patch={patch}
              fields={pickerFields}
              hasSample={hasSample}
              locked={preprocessingLocked}
              derivedFieldNames={derivedFieldNames}
            />

            {/* ── 3. Identity ── */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>
                Which column(s) uniquely identify a row?
                {recordKeyLocked && (
                  <Tooltip title={RECORD_KEY_LOCK_REASON}>
                    <span className={styles.lockBadge}>
                      <Lock size={11} /> locked
                    </span>
                  </Tooltip>
                )}
              </h3>
              <p className={styles.sectionHelp}>
                This is the upsert key: pushing a record with the same value overwrites the
                existing one instead of adding a second. For order lines or invoice items it is
                usually two or more columns used together.
              </p>

              {recordKeyLocked ? (
                <Alert severity="info" className={styles.inlineAlert}>
                  {RECORD_KEY_LOCK_REASON}
                </Alert>
              ) : (
                textLocked &&
                recordKeyPaths.length > 0 && (
                  <Alert severity="info" className={styles.inlineAlert}>
                    Records are already loaded under the current key. Changing it means the next
                    push writes new rows rather than updating these — the existing records keep
                    the identity they were ingested with.
                  </Alert>
                )
              )}

              <div className={styles.keyList}>
                {recordKeyPaths.map((path, index) => (
                  <div key={index} className={styles.keyRow}>
                    <span className={styles.keyOrder}>{index + 1}</span>
                    <PathField
                      value={path}
                      onChange={(v) => setRecordKeyAt(index, v)}
                      fields={pickerFields}
                      hasSample={hasSample}
                      label={index === 0 ? 'Column' : 'and'}
                      excludeLists
                      disabled={recordKeyLocked}
                    />
                    <div className={styles.keyActions}>
                      {recordKeyPaths.length > 1 && !recordKeyLocked && (
                        <>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                disabled={index === 0}
                                onClick={() => moveRecordKeyColumn(index, -1)}
                              >
                                <ChevronDown size={14} style={{ transform: 'rotate(180deg)' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                disabled={index === recordKeyPaths.length - 1}
                                onClick={() => moveRecordKeyColumn(index, 1)}
                              >
                                <ChevronDown size={14} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton size="small" onClick={() => removeRecordKeyColumn(index)}>
                            <Trash2 size={14} />
                          </IconButton>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {pendingKeyColumn && !recordKeyLocked && (
                  <div className={styles.keyRow}>
                    <span className={styles.keyOrder}>{recordKeyPaths.length + 1}</span>
                    <PathField
                      value=""
                      onChange={(v) => {
                        if (!v) return;
                        // Appends: index === current length.
                        setRecordKeyAt(recordKeyPaths.length, v);
                        setPendingKeyColumn(false);
                      }}
                      fields={pickerFields}
                      hasSample={hasSample}
                      label={recordKeyPaths.length === 0 ? 'Column' : 'and'}
                      placeholder="Pick the next column"
                      excludeLists
                    />
                    <div className={styles.keyActions}>
                      <IconButton
                        size="small"
                        onClick={() => setPendingKeyColumn(false)}
                        aria-label="Cancel this column"
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    </div>
                  </div>
                )}
                {/* One open row at a time keeps a composite key readable while
                    it is being built. */}
                {!recordKeyLocked && !pendingKeyColumn && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Plus size={14} />}
                    onClick={() => setPendingKeyColumn(true)}
                  >
                    {recordKeyPaths.length === 0 ? 'Pick a column' : 'Add another column'}
                  </Button>
                )}
                {recordKeyError && <span className={styles.fieldError}>{recordKeyError}</span>}

                {/* Repeated identities — the silent-data-loss case — are counted
                    by the live check on the right, which sees the records through
                    the same decoder the push does. A derived key cannot be
                    resolved in the browser at all, so it is the only source that
                    is right in every case. */}
                {derivedFieldNames.length > 0 && (
                  <span className={styles.fieldHint}>
                    You can use a derived field here — try{' '}
                    <code>
                      {preprocessingNamespace(config)}.{derivedFieldNames[0]}
                    </code>
                    .
                  </span>
                )}
              </div>

              <div className={styles.row1}>
                <PathField
                  value={config.title || ''}
                  onChange={(v) => patch((next) => { next.title = v; })}
                  fields={pickerFields}
                  hasSample={hasSample}
                  label="Title column"
                  helperText="Shown in the record list, and used to identify a record in the agent's answer."
                  excludeLists
                />
              </div>
            </section>

            {/* ── 3. Text ── */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>
                Text
                {textLocked && (
                  <Tooltip title={TEXT_LOCK_REASON}>
                    <span className={styles.lockBadge}>
                      <Lock size={11} /> locked
                    </span>
                  </Tooltip>
                )}
              </h3>
              {textLocked ? (
                <Alert severity="info" className={styles.inlineAlert}>
                  {TEXT_LOCK_REASON}
                </Alert>
              ) : (
                <p className={styles.sectionHelp}>
                  <strong>Embedded</strong> text is findable by meaning and costs embedding
                  budget. <strong>Stored only</strong> text is still readable — the agent filters
                  first, then reads the record directly — and costs nothing. A 100-page document
                  does not need embedding to be readable.
                </p>
              )}

              <div className={styles.cardList}>
                {text.map((entry, index) => (
                  <div key={index} className={styles.card}>
                    <div className={styles.cardRow}>
                      <PathField
                        value={entry.path || ''}
                        onChange={(v) => patchList('text', index, 'path', v)}
                        fields={pickerFields}
                        hasSample={hasSample}
                        label="Column"
                        disabled={textLocked}
                      />
                      <TextField
                        size="small"
                        label="Section heading"
                        value={entry.label || ''}
                        onChange={(e) => patchList('text', index, 'label', e.target.value)}
                        disabled={textLocked}
                        helperText="The model sees this above the text."
                      />
                    </div>
                    <div className={styles.switchRow}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(entry.embed)}
                            onChange={(e) => patchList('text', index, 'embed', e.target.checked)}
                            disabled={textLocked}
                          />
                        }
                        label={
                          <span className={styles.switchLabel}>
                            {entry.embed ? 'Searchable by meaning' : 'Stored, not searchable'}
                          </span>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(entry.html)}
                            onChange={(e) => patchList('text', index, 'html', e.target.checked)}
                            disabled={textLocked}
                          />
                        }
                        label={<span className={styles.switchLabel}>Strip HTML</span>}
                      />
                      <Tooltip title="Repeat this field at the top of every chunk. Use it for titles — a chunk cut from the middle of a long body is otherwise unattributable.">
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(entry.always)}
                              onChange={(e) => patchList('text', index, 'always', e.target.checked)}
                              disabled={textLocked}
                            />
                          }
                          label={<span className={styles.switchLabel}>Stamp on every chunk</span>}
                        />
                      </Tooltip>
                      <TextField
                        size="small"
                        type="number"
                        label="Order"
                        value={entry.priority ?? index + 1}
                        onChange={(e) =>
                          patchList('text', index, 'priority', Number(e.target.value))
                        }
                        disabled={textLocked}
                        className={styles.numField}
                      />
                      {!textLocked && (
                        <IconButton size="small" onClick={() => removeFrom('text', index)}>
                          <Trash2 size={14} />
                        </IconButton>
                      )}
                    </div>
                  </div>
                ))}
                {!textLocked && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Plus size={14} />}
                    onClick={() =>
                      patch((next) => {
                        next.text = [
                          ...(next.text || []),
                          {
                            path: '',
                            label: '',
                            priority: (next.text || []).length + 1,
                            embed: false,
                            html: false,
                            always: false,
                          },
                        ];
                      })
                    }
                  >
                    Add a text field
                  </Button>
                )}
              </div>
            </section>

            {/* ── 4. Dates ── */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Dates</h3>
              <p className={styles.sectionHelp}>
                Exactly one date is the primary one — it drives date filters, ordering and which
                month a record falls in.
              </p>
              <div className={styles.cardList}>
                {dates.map((entry, index) => (
                  <div key={index} className={styles.card}>
                    <div className={styles.cardRow}>
                      <TextField
                        size="small"
                        label="Name"
                        value={entry.name || ''}
                        onChange={(e) => patchList('dates', index, 'name', e.target.value)}
                      />
                      <PathField
                        value={entry.path || ''}
                        onChange={(v) => patchList('dates', index, 'path', v)}
                        fields={pickerFields}
                        hasSample={hasSample}
                        label="Column"
                      />
                    </div>
                    <div className={styles.switchRow}>
                      <FormControlLabel
                        control={
                          <Radio
                            size="small"
                            checked={Boolean(entry.primary)}
                            onChange={() =>
                              patch((next) => {
                                next.dates = next.dates.map((d, i) => ({
                                  ...d,
                                  primary: i === index,
                                }));
                              })
                            }
                          />
                        }
                        label={<span className={styles.switchLabel}>Primary</span>}
                      />
                      <Tooltip
                        title={
                          DATE_KIND_OPTIONS.find(
                            (o) => o.value === (entry.parse?.kind || 'auto')
                          )?.hint || ''
                        }
                      >
                        <TextField
                          select
                          size="small"
                          label="Format"
                          value={entry.parse?.kind || 'auto'}
                          onChange={(e) =>
                            patchList('dates', index, 'parse', {
                              ...(entry.parse || {}),
                              kind: e.target.value,
                            })
                          }
                          className={styles.selectField}
                        >
                          {DATE_KIND_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Tooltip>
                      {/* Numeric timestamps only. Microseconds is not exotic —
                          BigQuery and GA4 event_timestamp are `us`, and read as
                          milliseconds they land 50,000 years in the future. */}
                      {dateKindUsesUnit(entry.parse?.kind || 'auto') && (
                        <TextField
                          select
                          size="small"
                          label="Number means"
                          value={entry.parse?.unit || 'auto'}
                          onChange={(e) =>
                            patchList('dates', index, 'parse', {
                              ...(entry.parse || {}),
                              unit: e.target.value,
                            })
                          }
                          className={styles.selectField}
                        >
                          {DATE_UNIT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      {entry.parse?.kind === 'strptime' && (
                        <TextField
                          size="small"
                          label="Pattern"
                          value={entry.parse?.format || ''}
                          onChange={(e) =>
                            patchList('dates', index, 'parse', {
                              ...(entry.parse || {}),
                              format: e.target.value,
                            })
                          }
                        />
                      )}
                      {/* IST = 330. A record at 2026-01-31 20:00 UTC is FEBRUARY
                          in IST, and "articles from February" has to mean
                          February to the person reading the answer. */}
                      <Tooltip title="Minutes ahead of UTC. India is 330. Getting this wrong silently misfiles records at month boundaries.">
                        <TextField
                          size="small"
                          type="number"
                          label="UTC offset (min)"
                          value={entry.parse?.tz_offset_minutes ?? 0}
                          onChange={(e) =>
                            patchList('dates', index, 'parse', {
                              ...(entry.parse || {}),
                              tz_offset_minutes: Number(e.target.value),
                            })
                          }
                          className={styles.numField}
                        />
                      </Tooltip>
                      <TextField
                        select
                        size="small"
                        label="If unparseable"
                        value={entry.parse?.on_error || 'warn'}
                        onChange={(e) =>
                          patchList('dates', index, 'parse', {
                            ...(entry.parse || {}),
                            on_error: e.target.value,
                          })
                        }
                        className={styles.selectField}
                      >
                        <MenuItem value="warn">Keep record, no date</MenuItem>
                        <MenuItem value="null">Keep record, null date</MenuItem>
                        <MenuItem value="fail">Reject the record</MenuItem>
                      </TextField>
                      <IconButton size="small" onClick={() => removeFrom('dates', index)}>
                        <Trash2 size={14} />
                      </IconButton>
                    </div>
                  </div>
                ))}
                <Button
                  size="small"
                  variant="text"
                  startIcon={<Plus size={14} />}
                  onClick={() =>
                    patch((next) => {
                      const list = next.dates || [];
                      next.dates = [
                        ...list,
                        {
                          name: '',
                          path: '',
                          primary: list.length === 0,
                          // `auto` decides numeric-vs-ISO per value, which is
                          // what anything assembled from more than one source
                          // needs — and harmless when the column is consistent.
                          parse: {
                            kind: 'auto',
                            unit: 'auto',
                            tz_offset_minutes: 330,
                            on_error: 'warn',
                          },
                        },
                      ];
                    })
                  }
                >
                  Add a date
                </Button>
              </div>
            </section>

            {/* ── 5. Facets ── */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Facets — what the agent can filter on</h3>
              <p className={styles.sectionHelp}>
                Each facet becomes a filter key the model can use by name. Coverage next to each
                one comes from the live check on the right.
              </p>
              <div className={styles.cardList}>
                {facets.map((facet, index) => {
                  const coverage = dryRun?.report?.facet_coverage?.[facet.name];
                  const total = dryRun?.report?.counts?.total;
                  const pct = total ? (Number(coverage || 0) / total) * 100 : null;
                  const values = observedValues(facet.path);
                  const aliases = facet.normalize?.aliases || {};
                  const nameValid = !facet.name || FACET_NAME_RE.test(facet.name);

                  const expanded = Boolean(expandedFacets[index]);

                  return (
                    <div
                      key={index}
                      className={`${styles.card} ${facet.enabled === false ? styles.cardOff : ''}`}
                    >
                      {/* Collapsed by default. Thirteen facets fully expanded is
                          an eight-thousand-pixel page nobody can navigate — the
                          summary row carries what matters at a glance (name,
                          coverage, on/off) and the rest opens on demand. */}
                      <div className={styles.facetSummary}>
                        <button
                          type="button"
                          className={styles.facetToggle}
                          onClick={() =>
                            setExpandedFacets((prev) => ({ ...prev, [index]: !prev[index] }))
                          }
                          aria-expanded={expanded}
                        >
                          <ChevronDown
                            size={14}
                            style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                          />
                          <span className={styles.facetSummaryName}>
                            {facet.label || facet.name || 'Untitled facet'}
                          </span>
                          <code className={styles.facetSummaryKey}>{facet.name}</code>
                        </button>
                        {!nameValid && <span className={styles.facetInvalid}>invalid key</span>}
                        {pct !== null && (
                          <span
                            className={`${styles.coverageTag} ${
                              pct === 0 ? styles.coverageZero : ''
                            }`}
                          >
                            {pct === 0 ? 'never populated' : `${pct.toFixed(0)}% coverage`}
                          </span>
                        )}
                        <Tooltip title={facet.enabled === false ? 'Turned off' : 'In use'}>
                          <Switch
                            size="small"
                            checked={facet.enabled !== false}
                            onChange={(e) =>
                              patchList('facets', index, 'enabled', e.target.checked)
                            }
                          />
                        </Tooltip>
                        <IconButton size="small" onClick={() => removeFrom('facets', index)}>
                          <Trash2 size={14} />
                        </IconButton>
                      </div>

                      {expanded && (
                      <>
                      <div className={styles.cardRow}>
                        <TextField
                          size="small"
                          label="Filter key"
                          value={facet.name || ''}
                          onChange={(e) =>
                            patchList('facets', index, 'name', e.target.value)
                          }
                          onBlur={(e) =>
                            patchList('facets', index, 'name', slugifyFacetName(e.target.value))
                          }
                          error={!nameValid}
                          helperText={
                            nameValid
                              ? 'What the model types to filter on this.'
                              : 'Lowercase letters, digits and underscores only, starting with a letter.'
                          }
                          InputProps={{ className: styles.monoInput }}
                        />
                        <TextField
                          size="small"
                          label="Label"
                          value={facet.label || ''}
                          onChange={(e) => patchList('facets', index, 'label', e.target.value)}
                          helperText="Shown to people, and to the model."
                        />
                        <PathField
                          value={facet.path || ''}
                          onChange={(v) => patchList('facets', index, 'path', v)}
                          fields={pickerFields}
                          hasSample={hasSample}
                          label="Column"
                          showListHint={facet.cardinality !== 'many'}
                        />
                      </div>

                      <div className={styles.switchRow}>
                        <TextField
                          select
                          size="small"
                          label="Values per record"
                          value={facet.cardinality || 'one'}
                          onChange={(e) =>
                            patchList('facets', index, 'cardinality', e.target.value)
                          }
                          className={styles.selectField}
                          helperText="Wrong here makes coverage wrong."
                        >
                          <MenuItem value="one">At most one</MenuItem>
                          <MenuItem value="many">Several</MenuItem>
                        </TextField>
                        <TextField
                          select
                          size="small"
                          label="Case"
                          value={facet.normalize?.case || 'none'}
                          onChange={(e) =>
                            patchList('facets', index, 'normalize', {
                              ...(facet.normalize || {}),
                              case: e.target.value === 'none' ? null : e.target.value,
                            })
                          }
                          className={styles.selectField}
                        >
                          <MenuItem value="none">Leave as is</MenuItem>
                          <MenuItem value="lower">lowercase</MenuItem>
                          <MenuItem value="upper">UPPERCASE</MenuItem>
                          <MenuItem value="title">Title Case</MenuItem>
                        </TextField>
                        {/* Enabled, coverage and delete live in the summary row
                            above, so they stay visible while collapsed. */}
                        {pct === 0 && (
                          <span className={styles.coverageHint}>
                            Never populated in the sample — check the column.
                          </span>
                        )}
                      </div>

                      <TextField
                        size="small"
                        fullWidth
                        label="Caveat (optional)"
                        value={facet.note || ''}
                        onChange={(e) => patchList('facets', index, 'note', e.target.value)}
                        helperText="Shown verbatim wherever this facet appears. For fields that are populated and still misleading."
                      />

                      {/* ── Aliases: the highest-leverage setting here ── */}
                      <div className={styles.aliasBlock}>
                        <div className={styles.aliasHead}>
                          <Wand2 size={13} />
                          <strong>Merge different spellings</strong>
                          <span>
                            Without this, “SEBI” and “Securities and Exchange Board of India”
                            count as two different values — and every count over this facet is
                            wrong.
                          </span>
                        </div>

                        {Object.entries(aliases).length > 0 && (
                          <div className={styles.aliasList}>
                            {Object.entries(aliases).map(([from, to]) => (
                              <div key={from} className={styles.aliasRow}>
                                <code>{from}</code>
                                <span>→</span>
                                <code>{to}</code>
                                <IconButton
                                  size="small"
                                  onClick={() => setAlias(index, from, null)}
                                >
                                  <Trash2 size={12} />
                                </IconButton>
                              </div>
                            ))}
                          </div>
                        )}

                        {values.length > 0 ? (
                          <>
                            <span className={styles.aliasHint}>
                              Values seen in your sample — click one to map it onto another:
                            </span>
                            <div className={styles.valueChips}>
                              {values.map((v) => (
                                <Chip
                                  key={v}
                                  size="small"
                                  label={v.length > 40 ? `${v.slice(0, 40)}…` : v}
                                  variant={aliases[v] ? 'filled' : 'outlined'}
                                  onClick={() =>
                                    setAliasDrafts((prev) => ({
                                      ...prev,
                                      [index]: { from: v, to: aliases[v] || '' },
                                    }))
                                  }
                                  className={styles.valueChip}
                                />
                              ))}
                            </div>
                          </>
                        ) : (
                          <span className={styles.aliasHint}>
                            Load a sample file to see the actual values in your data here.
                          </span>
                        )}

                        {aliasDrafts[index] && (
                          <div className={styles.aliasDraft}>
                            <code>{aliasDrafts[index].from}</code>
                            <span>→</span>
                            <TextField
                              size="small"
                              placeholder="Standard value"
                              value={aliasDrafts[index].to}
                              onChange={(e) =>
                                setAliasDrafts((prev) => ({
                                  ...prev,
                                  [index]: { ...prev[index], to: e.target.value },
                                }))
                              }
                            />
                            <Button
                              size="small"
                              variant="contained"
                              className={sharedStyles.primaryBtn}
                              onClick={() => {
                                setAlias(index, aliasDrafts[index].from, aliasDrafts[index].to);
                                setAliasDrafts((prev) => {
                                  const next = { ...prev };
                                  delete next[index];
                                  return next;
                                });
                              }}
                            >
                              Map
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() =>
                                setAliasDrafts((prev) => {
                                  const next = { ...prev };
                                  delete next[index];
                                  return next;
                                })
                              }
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                      </>
                      )}
                    </div>
                  );
                })}
                <Button
                  size="small"
                  variant="text"
                  startIcon={<Plus size={14} />}
                  onClick={() =>
                    patch((next) => {
                      next.facets = [...(next.facets || []), emptyFacet((next.facets || []).length)];
                    })
                  }
                >
                  Add a facet
                </Button>
              </div>
            </section>

            {/* ── 6. Relations: accepted, inert ── */}
            <section className={styles.section}>
              <button
                type="button"
                className={styles.collapseToggle}
                onClick={() => toggleSection('relations')}
              >
                <ChevronDown
                  size={14}
                  style={{
                    transform: openSections.relations ? 'rotate(0deg)' : 'rotate(-90deg)',
                  }}
                />
                Relations ({(config.relations || []).length})
              </button>
              {openSections.relations && (
                <>
                  <Alert severity="info" className={styles.inlineAlert}>
                    Stored for a future version. Agents cannot follow record-to-record links
                    today — related records are found through shared facet values instead. Fill
                    this in if your data has it, so the mapping survives to a later version.
                  </Alert>
                  <div className={styles.cardList}>
                    {(config.relations || []).map((rel, index) => (
                      <div key={index} className={styles.cardRow}>
                        <TextField
                          size="small"
                          label="Name"
                          value={rel.name || ''}
                          onChange={(e) => patchList('relations', index, 'name', e.target.value)}
                        />
                        <PathField
                          value={rel.path || ''}
                          onChange={(v) => patchList('relations', index, 'path', v)}
                          fields={pickerFields}
                          hasSample={hasSample}
                          label="Column"
                        />
                        <IconButton size="small" onClick={() => removeFrom('relations', index)}>
                          <Trash2 size={14} />
                        </IconButton>
                      </div>
                    ))}
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Plus size={14} />}
                      onClick={() =>
                        patch((next) => {
                          next.relations = [
                            ...(next.relations || []),
                            { name: '', path: '', target: 'self' },
                          ];
                        })
                      }
                    >
                      Add a relation
                    </Button>
                  </div>
                </>
              )}
            </section>

            {/* ── 7. Advanced ── */}
            <section className={styles.section}>
              <button
                type="button"
                className={styles.collapseToggle}
                onClick={() => toggleSection('advanced')}
              >
                <ChevronDown
                  size={14}
                  style={{
                    transform: openSections.advanced ? 'rotate(0deg)' : 'rotate(-90deg)',
                  }}
                />
                Ignored columns ({(config.ignore || []).length})
              </button>
              {openSections.advanced && (
                <div className={styles.ignoreBlock}>
                  <p className={styles.sectionHelp}>
                    Columns you know about and don’t want reported as unmapped.
                  </p>
                  <div className={styles.valueChips}>
                    {(config.ignore || []).map((field) => (
                      <Chip
                        key={field}
                        size="small"
                        label={field}
                        onDelete={() =>
                          patch((next) => {
                            next.ignore = (next.ignore || []).filter((i) => i !== field);
                          })
                        }
                      />
                    ))}
                    {(config.ignore || []).length === 0 && (
                      <span className={styles.aliasHint}>Nothing ignored.</span>
                    )}
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* ══ Right: live dry-run ══ */}
      <aside className={styles.checkColumn}>
        <div className={styles.checkInner}>
          <div className={styles.checkHead}>
            <h3 className={styles.sectionTitle}>Live check</h3>
            {dryRunning && <CircularProgress size={14} />}
          </div>

          {!samples?.length ? (
            <div className={styles.checkEmpty}>
              <UploadCloud size={30} color="var(--text-secondary)" />
              <strong>Check this mapping against real data</strong>
              <p>
                Load a sample export and every edit is validated against it instantly — which
                facets actually populate, which columns aren’t mapped, and what the agent will
                receive. Nothing is uploaded or written.
              </p>
              <Button
                size="small"
                variant="outlined"
                className={sharedStyles.outlineBtn}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose sample file(s)
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  readSample(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>
          ) : dryRunError ? (
            <Alert severity="warning">{dryRunError}</Alert>
          ) : dryRun ? (
            <>
              <KgDryRunReport
                result={dryRun}
                totalFacets={facets.length}
                onAddFacet={handleAddFacet}
                onAddIgnore={handleAddIgnore}
                recordKeyLabel={recordKeyLabel}
              />

              {/* The raw column list, from the same /inspect that feeds the
                  pickers. Folded away because the dry-run is what you watch
                  while editing — this is what you open when a path won't
                  resolve and you need to see what is actually there. */}
              <section className={styles.section}>
                <button
                  type="button"
                  className={styles.collapseToggle}
                  onClick={() => toggleSection('columns')}
                >
                  <ChevronDown
                    size={14}
                    style={{
                      transform: openSections.columns ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                  />
                  Columns in your data ({sampleFields.length})
                  {inspecting && <CircularProgress size={12} />}
                </button>
                {openSections.columns && inspection && (
                  <KgInspectPanel inspection={inspection} compact />
                )}
              </section>
            </>
          ) : (
            <div className={styles.checkEmpty}>
              <CircularProgress size={20} />
              <p>Checking…</p>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default MappingTab;
